import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  LinearProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  Insights as InsightsIcon,
  WorkspacePremium as WorkspacePremiumIcon,
  ReceiptLong as ReceiptLongIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import API_BASE_URL from "../../utils/api";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
const GOLD = "#FFD700";
const QUARTER_RANGES = {
  1: "Jan 1 - Mar 31",
  2: "Apr 1 - Jun 30",
  3: "Jul 1 - Sep 30",
  4: "Oct 1 - Dec 31",
};

function describeQuarter(quarterKey) {
  if (!quarterKey) return "";
  const [yearStr, qStr] = quarterKey.split("-Q");
  const year = Number(yearStr);
  const q = Number(qStr);
  if (!year || !q || !QUARTER_RANGES[q]) return "";
  return `${QUARTER_RANGES[q]} ${year}`;
}

export default function GrantsDashboard() {
  useEffect(() => {
    document.title = "Grants Dashboard - MS Digital Hub";
  }, []);

  const [loading, setLoading] = useState(true);
  const [series, setSeries] = useState([]);
  const [dailySeries, setDailySeries] = useState([]); // 👈 move this up
  const [selectedQuarter, setSelectedQuarter] = useState("ALL");

  const hasDaily = dailySeries.length > 0;
  const quarterKeys = series.map((q) => q.quarterKey);

  // Filter daily data based on selected quarter
  const filteredDaily = !hasDaily
    ? []
    : selectedQuarter === "ALL"
      ? dailySeries
      : dailySeries.filter((d) => d.quarterKey === selectedQuarter);

  const totalDocs = series.reduce((sum, q) => sum + (q.digitizedCount || 0), 0);
  const currentQuarter = series.find((q) => q.isCurrent);
  const currentQuarterDocs = currentQuarter?.digitizedCount || 0;
  const currentQuarterLabel = currentQuarter?.quarterKey || "Current quarter";
  const [currentCount, setCurrentCount] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [files, setFiles] = useState([]);
  const [notes, setNotes] = useState([]);
  const [showAllNotes, setShowAllNotes] = useState(false);
  const [editNoteOpen, setEditNoteOpen] = useState(false);
  const [editNoteId, setEditNoteId] = useState(null);
  const [editNoteText, setEditNoteText] = useState("");

  const [deleteNoteId, setDeleteNoteId] = useState(null);

  const [editReceiptOpen, setEditReceiptOpen] = useState(false);
  const [editReceiptId, setEditReceiptId] = useState(null);
  const [editPurchaseType, setEditPurchaseType] = useState("");
  const [editPurchaseDate, setEditPurchaseDate] = useState("");
  const [editAmount, setEditAmount] = useState("");

  const [deleteReceiptId, setDeleteReceiptId] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [purchaseType, setPurchaseType] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [amount, setAmount] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  // Grant date window, in YYYY-MM-DD (for string comparisons)
  const GRANT_START_ISO = "2025-12-01";
  const GRANT_END_ISO = "2027-06-30";
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  // Today, for "no future dates" rule
  const todayISO = new Date().toISOString().slice(0, 10);

  // max date user can pick = min(today, GRANT_END)
  const maxPurchaseDate = todayISO > GRANT_END_ISO ? GRANT_END_ISO : todayISO;
  async function fetchAll() {
    setLoading(true);
    setErr("");
    try {
      const token = localStorage.getItem("token");
      const [p, r, n] = await Promise.all([
        axios.get(`${API_BASE_URL}/grants/progress`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE_URL}/grants/receipts`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE_URL}/grants/notes`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const progressPayload = p.data || {};
      const quarters = Array.isArray(progressPayload.data)
        ? progressPayload.data
        : progressPayload.quarters || [];
      const daily = progressPayload.daily || [];

      setSeries(quarters);
      setDailySeries(daily);
      setFiles(r.data.files || []);
      setNotes(n.data.notes || []);
    } catch (e) {
      setErr(
        e.response?.data?.error ||
          "We couldn't load your grant dashboard. Please refresh the page or try again in a few minutes."
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchAll();
  }, []);

  const handleSaveCount = async () => {
    setErr("");
    setMsg("");

    const count = Number(currentCount);

    if (Number.isNaN(count) || count < 0) {
      setErr("Please enter a non-negative number of new documents.");
      setCurrentCount("");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/grants/progress`,
        { digitizedCount: count },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMsg(
        `Saved! We recorded ${count} new document${
          count === 1 ? "" : "s"
        } for this quarter.`
      );
      await fetchAll();
    } catch (e) {
      setErr(
        e.response?.data?.error ||
          "We couldn't save your update right now. Please try again in a few minutes."
      );
    } finally {
      // Clear textbox regardless of success/failure
      setCurrentCount("");
    }
  };
  const handleConfirmReset = async () => {
    setErr("");
    setMsg("");
    setResetDialogOpen(false); // close the dialog

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/grants/progress/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMsg(
        "Your current quarter's progress has been reset to zero. You can start entering new documents again."
      );
      await fetchAll();
    } catch (e) {
      setErr(
        e.response?.data?.error ||
          "We couldn't reset your progress right now. Please try again in a few minutes."
      );
    } finally {
      setCurrentCount("");
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!receiptFile) {
      setErr("Please attach a receipt file.");
      return;
    }

    // Validate purchaseDate (if provided)
    if (purchaseDate) {
      if (purchaseDate < GRANT_START_ISO || purchaseDate > maxPurchaseDate) {
        setErr(
          `Purchase date must be between Dec 1, 2025 and ${
            maxPurchaseDate === GRANT_END_ISO ? "Jun 30, 2027" : "today"
          }.`
        );
        return;
      }
    }

    setUploading(true);
    setErr("");
    setMsg("");
    try {
      const token = localStorage.getItem("token");
      const form = new FormData();
      form.append("purchaseType", purchaseType);
      form.append("purchaseDate", purchaseDate);
      form.append("amount", amount);
      form.append("file", receiptFile);

      await axios.post(`${API_BASE_URL}/grants/receipts`, form, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      setMsg("Receipt uploaded successfully.");
      setUploadOpen(false);
      setPurchaseType("");
      setPurchaseDate("");
      setAmount("");
      setReceiptFile(null);
      await fetchAll();
    } catch (e) {
      setErr(
        e.response?.data?.error ||
          "We couldn't upload your receipt. Please check the file and try again."
      );
    } finally {
      setUploading(false);
    }
  };
  const canSaveNote = noteText.trim().length > 0 && !savingNote;

  const saveNoteTooltip = !noteText.trim()
    ? "Write a project update to enable saving."
    : savingNote
      ? "Saving your note..."
      : "Save this note to your project timeline.";

  const handleSaveNote = async () => {
    setErr("");
    setMsg("");

    const text = noteText.trim();
    if (!text) {
      setErr("Please write a note before saving.");
      return;
    }

    setSavingNote(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/grants/notes`,
        { text },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMsg("Project note saved.");
      setNoteText("");
      await fetchAll();
    } catch (e) {
      setErr(
        e.response?.data?.error ||
          "We couldn't save your note right now. Please try again."
      );
    } finally {
      setSavingNote(false);
    }
  };
  const openEditNote = (n) => {
    setEditNoteId(n._id);
    setEditNoteText(n.text || "");
    setEditNoteOpen(true);
  };

  const handleUpdateNote = async () => {
    setErr("");
    setMsg("");
    const text = editNoteText.trim();
    if (!text) return setErr("Note cannot be empty.");

    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_BASE_URL}/grants/notes/${editNoteId}`,
        { text },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMsg("Note updated.");
      setEditNoteOpen(false);
      setEditNoteId(null);
      setEditNoteText("");
      await fetchAll();
    } catch (e) {
      setErr(e.response?.data?.error || "Could not update note.");
    }
  };

  const handleDeleteNote = async () => {
    setErr("");
    setMsg("");
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE_URL}/grants/notes/${deleteNoteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMsg("Note deleted.");
      setDeleteNoteId(null);
      await fetchAll();
    } catch (e) {
      setErr(e.response?.data?.error || "Could not delete note.");
    }
  };

  const openEditReceipt = (f) => {
    setEditReceiptId(f._id); // IMPORTANT: make sure backend returns _id in /receipts list (see note below)
    setEditPurchaseType(f.purchaseType || "");
    setEditPurchaseDate(f.purchaseDate || "");
    setEditAmount(String(f.amount ?? ""));
    setEditReceiptOpen(true);
  };

  const handleUpdateReceipt = async () => {
    setErr("");
    setMsg("");

    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_BASE_URL}/grants/receipts/${editReceiptId}`,
        {
          purchaseType: editPurchaseType,
          purchaseDate: editPurchaseDate,
          amount: editAmount,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMsg("Receipt updated.");
      setEditReceiptOpen(false);
      setEditReceiptId(null);
      await fetchAll();
    } catch (e) {
      setErr(e.response?.data?.error || "Could not update receipt.");
    }
  };

  const handleDeleteReceipt = async () => {
    setErr("");
    setMsg("");
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE_URL}/grants/receipts/${deleteReceiptId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMsg("Receipt deleted.");
      setDeleteReceiptId(null);
      await fetchAll();
    } catch (e) {
      setErr(e.response?.data?.error || "Could not delete receipt.");
    }
  };

  useEffect(() => {
    if (!msg && !err) return;
    const t = setTimeout(() => {
      setMsg("");
      setErr("");
    }, 4000); // 4 seconds
    return () => clearTimeout(t);
  }, [msg, err]);
  const canResetCurrentQuarter = currentQuarterDocs > 0;

  const isSaveDisabled = currentCount === "";
  const saveTooltip = isSaveDisabled
    ? "Enter how many new documents you've digitized to enable saving."
    : "Add this number to your running total for the current quarter.";

  const resetTooltip = canResetCurrentQuarter
    ? "Reset only this quarter's total and daily history to 0."
    : "You can only reset after you've recorded documents in this quarter.";

  const canSubmitReceipt = !!receiptFile && !uploading;
  const submitTooltip = !receiptFile
    ? "Attach a receipt file to enable submission."
    : uploading
      ? "Uploading your receipt. Please wait..."
      : "Upload this receipt and save it to your grant records.";

  const cancelUploadTooltip = uploading
    ? "Please wait until the upload finishes."
    : "Close this form without uploading a receipt.";

  const resetDialogCancelTooltip =
    "Close this dialog without resetting this quarter.";
  const resetDialogConfirmTooltip =
    "Set this quarter's total and daily history to 0. Previous quarters stay unchanged.";
  return (
    <Box
      sx={{
        width: "auto",

        padding: "0px 20px",
        margin: "0px auto",
        padding: { xs: "20px", sm: "30px", md: "1px 80px" },
        border: "none",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
      }}
    >
      <Box
        sx={{
          mb: 3,
          pb: 2,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.75,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <WorkspacePremiumIcon sx={{ color: GOLD }} />
            <Typography variant="h4">Grants Dashboard</Typography>
          </Box>

          <Typography variant="body2" color="text.secondary">
            Track your grant&apos;s digitized documents each quarter and keep
            all funding receipts organized in one place.
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              width: "fit-content",
              mx: "auto",
              mt: 0.5,
              px: 1.8,
              py: 0.5,
              borderRadius: 999,
              bgcolor: "#fff9d6",
              border: `1px solid ${GOLD}66`,
              fontWeight: 500,
            }}
          >
            Visible to you and program administrators only.
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ mt: 1 }}
          >
            <Chip
              label={`Total documents digitized: ${totalDocs}`}
              size="small"
              sx={{
                bgcolor: "#f5f5f5",
                borderRadius: 999,
                fontWeight: 500,
              }}
            />
            <Chip
              label={`${currentQuarterLabel}: ${currentQuarterDocs} docs`}
              size="small"
              sx={{
                bgcolor: "#fff9d6",
                borderRadius: 999,
                border: `1px solid ${GOLD}66`,
                fontWeight: 500,
              }}
            />
          </Stack>
        </Box>
      </Box>
      {err && (
        <Alert sx={{ mb: 2 }} severity="error">
          {err}
        </Alert>
      )}
      {msg && (
        <Alert sx={{ mb: 2 }} severity="success">
          {msg}
        </Alert>
      )}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <InsightsIcon sx={{ color: GOLD }} />
              <Typography variant="h6">
                Digitized Documents over time
              </Typography>
            </Box>
          </Stack>
          {hasDaily && (
            <>
              <Box sx={{ mb: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
                <Chip
                  size="small"
                  label="Overview - all quarters"
                  onClick={() => setSelectedQuarter("ALL")}
                  sx={{
                    borderRadius: 999,
                    bgcolor: selectedQuarter === "ALL" ? "#fff9d6" : "#f5f5f5",
                    border: `1px solid ${
                      selectedQuarter === "ALL" ? GOLD : "#ddd"
                    }`,
                    fontWeight: selectedQuarter === "ALL" ? 600 : 400,
                  }}
                />

                {series.map((q) => (
                  <Chip
                    key={q.quarterKey}
                    size="small"
                    label={`${q.quarterKey} - ${q.digitizedCount || 0} docs`}
                    onClick={() => setSelectedQuarter(q.quarterKey)}
                    sx={{
                      borderRadius: 999,
                      bgcolor:
                        selectedQuarter === q.quarterKey
                          ? "#fff9d6"
                          : "#f5f5f5",
                      border: `1px solid ${
                        selectedQuarter === q.quarterKey ? GOLD : "#ddd"
                      }`,
                      fontWeight: selectedQuarter === q.quarterKey ? 600 : 400,
                    }}
                  />
                ))}
              </Box>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                Quarters use calendar months: Q1 = Jan-Mar, Q2 = Apr-Jun, Q3 =
                Jul-Sep, Q4 = Oct-Dec. Your grant runs from Dec 1, 2025 to Jun
                30, 2027.
              </Typography>

              {selectedQuarter !== "ALL" && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  {selectedQuarter} covers {describeQuarter(selectedQuarter)}.
                </Typography>
              )}
            </>
          )}

          {loading ? (
            <LinearProgress />
          ) : hasDaily ? (
            filteredDaily.length > 0 ? (
              <Box sx={{ height: { xs: 220, md: 280 }, mt: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={filteredDaily} // use filteredDaily (ALL or specific quarter)
                    margin={{ top: 10, right: 16, left: 0, bottom: 10 }}
                  >
                    <defs>
                      <linearGradient
                        id="colorDocs"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="5%" stopColor={GOLD} stopOpacity={0.9} />
                        <stop offset="95%" stopColor={GOLD} stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      padding={{ left: 20, right: 20 }}
                      tickFormatter={(value) => {
                        const d = new Date(value);
                        if (Number.isNaN(d.getTime())) return value;
                        return d.toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          timeZone: "UTC",
                        });
                      }}
                      label={{
                        value: "Date",
                        position: "insideBottom",
                        offset: -5,
                      }}
                    />
                    <YAxis
                      allowDecimals={false}
                      label={{
                        value: "Total docs",
                        angle: -90,
                        position: "insideLeft",
                      }}
                    />
                    <RechartsTooltip
                      labelFormatter={(value) => {
                        const d = new Date(value);
                        if (Number.isNaN(d.getTime())) return value;
                        return d.toLocaleDateString("en-US", {
                          timeZone: "UTC",
                        });
                      }}
                      formatter={(val, _name, ctx) => {
                        const point = ctx?.payload;
                        const baseLabel = "Cumulative total";
                        if (point?.deltaSum != null && point?.updates != null) {
                          const extra = ` (added ${point.deltaSum} doc${
                            point.deltaSum === 1 ? "" : "s"
                          } in ${point.updates} update${
                            point.updates === 1 ? "" : "s"
                          })`;
                          return [`${val}${extra}`, baseLabel];
                        }
                        return [val, baseLabel];
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="total"
                      stroke={GOLD}
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorDocs)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              // this handles quarters that exist but have no daily data yet
              <Alert severity="info">
                {selectedQuarter === "ALL"
                  ? "No activity recorded yet."
                  : `No activity recorded in ${selectedQuarter}.`}
              </Alert>
            )
          ) : series.length ? (
            // Fallback: if there are no daily logs at all, show simple quarter summary chart
            <Box sx={{ height: { xs: 220, md: 260 }, mt: 1 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={series}
                  margin={{ top: 10, right: 16, left: 0, bottom: 10 }}
                >
                  <defs>
                    <linearGradient id="colorDocs" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={GOLD} stopOpacity={0.9} />
                      <stop offset="95%" stopColor={GOLD} stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="quarterKey"
                    padding={{ left: 20, right: 20 }}
                    label={{
                      value: "Quarter",
                      position: "insideBottom",
                      offset: -5,
                    }}
                  />
                  <YAxis
                    allowDecimals={false}
                    label={{
                      value: "Docs",
                      angle: -90,
                      position: "insideLeft",
                    }}
                  />
                  <RechartsTooltip />
                  <Area
                    type="monotone"
                    dataKey="digitizedCount"
                    stroke={GOLD}
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorDocs)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Alert severity="info">
              No progress yet — add your current quarter's total below.
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              mt: 3,
              alignItems: "center",
            }}
          >
            <TextField
              type="number"
              label="New documents this update"
              placeholder="e.g., 12"
              helperText="Enter only how many new items you digitized since your last update."
              value={currentCount}
              onChange={(e) => setCurrentCount(e.target.value)}
              inputProps={{ min: 0 }}
              size="small"
              sx={{
                "& .MuiInputBase-input::placeholder": {
                  color: "text.disabled",
                  opacity: 1,
                },
                "& .MuiInputBase-input": {
                  color: "text.secondary",
                },
                "& .MuiFormHelperText-root": {
                  color: "text.secondary",
                },
              }}
            />

            <Tooltip title={saveTooltip} arrow>
              <span>
                <Button
                  variant="contained"
                  onClick={handleSaveCount}
                  disabled={isSaveDisabled}
                  sx={{
                    bgcolor: GOLD,
                    color: "#000",
                    "&:hover": { bgcolor: "#e6c200" },
                  }}
                >
                  Save
                </Button>
              </span>
            </Tooltip>

            <Tooltip title={resetTooltip} arrow>
              <span>
                <Button
                  variant="text"
                  color="error"
                  onClick={() => setResetDialogOpen(true)}
                  disabled={!canResetCurrentQuarter}
                  sx={{ textTransform: "none" }}
                >
                  Reset current quarter to 0
                </Button>
              </span>
            </Tooltip>
          </Box>
          <Typography variant="body2" sx={{ mt: 1.5, color: "text.secondary" }}>
            <Typography
              variant="body2"
              sx={{ mt: 1.5, color: "text.secondary" }}
            >
              Each time you click <b>Save</b>, we add your new documents to your
              running total for the <b>current quarter</b>.
            </Typography>
          </Typography>
        </CardContent>
      </Card>
      <Card
        sx={{
          mb: 3,
          borderRadius: 2,
          boxShadow: 2,
          border: "1px solid #eee",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
            bgcolor: "#fff9d6",
            borderBottom: `1px solid ${GOLD}33`,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
            <Typography
              variant="h6"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 999,
                  background: GOLD,
                  display: "inline-block",
                }}
              />
              Project Notes
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Share updates anytime — discoveries, milestones.
            </Typography>
          </Box>
        </Box>

        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: "1px solid #eee",
              bgcolor: "#fafafa",
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Add a note
            </Typography>

            <TextField
              label="What would you like to share?"
              placeholder="Example: We finished digitizing three boxes of photographs today."
              multiline
              minRows={4}
              fullWidth
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              sx={{
                "& .MuiInputBase-input::placeholder": {
                  color: "text.disabled",
                  opacity: 1,
                },
              }}
            />

            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="caption" color="text.secondary"></Typography>

              <Tooltip title={saveNoteTooltip} arrow>
                <span>
                  <Button
                    variant="contained"
                    disabled={!canSaveNote}
                    onClick={handleSaveNote}
                    sx={{
                      bgcolor: GOLD,
                      color: "#000",
                      "&:hover": { bgcolor: "#e6c200" },
                    }}
                  >
                    {savingNote ? "Saving…" : "Add note"}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mb: 1.25,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="subtitle2">Recent notes</Typography>

              {notes.length > 5 && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setShowAllNotes((s) => !s)}
                  sx={{ textTransform: "none" }}
                >
                  {showAllNotes ? "Show less" : "Show more"}
                </Button>
              )}
            </Box>

            {notes.length === 0 ? (
              <Alert severity="info">No project notes yet.</Alert>
            ) : (
              <Stack spacing={1.5}>
                {(showAllNotes ? notes : notes.slice(0, 5)).map((n) => (
                  <Box
                    key={n._id}
                    sx={{
                      border: "1px solid #eee",
                      borderRadius: 2,
                      p: 1.5,
                      bgcolor: "#ffffff",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 6,
                        bgcolor: "#fff1a8",
                        borderRight: `1px solid ${GOLD}33`,
                      }}
                    />

                    <Box sx={{ pl: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {new Date(n.createdAt).toLocaleString()}
                        </Typography>

                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => openEditNote(n)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteNoteId(n._id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.75,
                          whiteSpace: "pre-wrap",
                          lineHeight: 1.7,
                        }}
                      >
                        {n.text}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ReceiptLongIcon sx={{ color: GOLD }} />
              <Typography variant="h6">Receipts</Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              disabled={uploading}
              size="small"
              onClick={() => setUploadOpen(true)}
            >
              {uploading ? "Uploading…" : "Upload receipt"}
            </Button>
          </Stack>

          <Box sx={{ mt: 1 }}>
            {files.length === 0 ? (
              <Alert severity="info">No receipts uploaded yet.</Alert>
            ) : (
              <Stack spacing={1.5}>
                {files.map((f) => (
                  <Box
                    key={f._id || f.url}
                    sx={{
                      border: "1px solid #eee",
                      borderRadius: 2,
                      p: 1.5,
                      bgcolor: "#fff",
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2">
                        {f.purchaseType || "Receipt"}
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        Amount:{" "}
                        <b>
                          {f.amount ? `$${Number(f.amount).toFixed(2)}` : "-"}
                        </b>
                        {" · "}
                        Date purchased:{" "}
                        <b>
                          {f.purchaseDate
                            ? new Date(f.purchaseDate).toLocaleDateString()
                            : "-"}
                        </b>
                        {" · "}
                        Uploaded:{" "}
                        <b>
                          {f.lastModified
                            ? new Date(f.lastModified).toLocaleString()
                            : "-"}
                        </b>
                      </Typography>

                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <a href={f.url} target="_blank" rel="noreferrer">
                          View receipt file ({(f.size / 1024).toFixed(1)} KB)
                        </a>
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => openEditReceipt(f)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteReceiptId(f._id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </CardContent>
      </Card>
      <Dialog
        open={uploadOpen}
        onClose={() => !uploading && setUploadOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Upload receipt</DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={handleUploadSubmit}>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="What type of purchase?"
                fullWidth
                value={purchaseType}
                onChange={(e) => setPurchaseType(e.target.value)}
              />
              <TextField
                label="Date purchased"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                inputProps={{
                  min: GRANT_START_ISO,
                  max: maxPurchaseDate,
                }}
                helperText={`Purchase date must be between Dec 1, 2025 and Jun 30, 2027 (not in the future).`}
              />
              <TextField
                label="Amount"
                type="number"
                fullWidth
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />

              <Tooltip
                title="Attach a digital copy of your receipt or invoice (PDF, image, or other file format is fine)."
                arrow
              >
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                >
                  {receiptFile ? "Change receipt file" : "Attach receipt"}
                  <input
                    type="file"
                    hidden
                    onChange={(e) =>
                      setReceiptFile(e.target.files?.[0] || null)
                    }
                  />
                </Button>
              </Tooltip>
              {receiptFile && (
                <Typography variant="body2" color="text.secondary">
                  Selected: {receiptFile.name}
                </Typography>
              )}

              <Typography variant="caption" color="text.secondary">
                You can upload any clear digital copy of a grant-related
                receipt, invoice, or payment confirmation (PDFs, images, or
                other common file types are all okay).
              </Typography>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Tooltip title={cancelUploadTooltip} arrow>
            <span>
              <Button onClick={() => setUploadOpen(false)} disabled={uploading}>
                Cancel
              </Button>
            </span>
          </Tooltip>

          <Tooltip title={submitTooltip} arrow>
            <span>
              <Button
                onClick={handleUploadSubmit}
                variant="contained"
                disabled={!canSubmitReceipt}
                sx={{
                  bgcolor: GOLD,
                  color: "#000",
                  "&:hover": { bgcolor: "#e6c200" },
                }}
              >
                {uploading ? "Uploading…" : "Submit"}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
      <Dialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        BackdropProps={{
          sx: {
            backdropFilter: "blur(3px)", // blur the background
            backgroundColor: "rgba(0,0,0,0.3)",
          },
        }}
      >
        <DialogTitle>Reset current quarter?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            This will reset <b>only your current quarter&apos;s total</b> to 0
            and clear its daily history. Previous quarters&apos; totals and
            history will stay exactly the same.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.5, color: "text.secondary" }}>
            You can start entering new documents for this quarter again after
            the reset.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Tooltip title={resetDialogCancelTooltip} arrow>
            <span>
              <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
            </span>
          </Tooltip>

          <Tooltip title={resetDialogConfirmTooltip} arrow>
            <span>
              <Button
                onClick={handleConfirmReset}
                color="error"
                variant="contained"
                sx={{ textTransform: "none" }}
              >
                Yes, reset to 0
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
      <Dialog
        open={editNoteOpen}
        onClose={() => setEditNoteOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit note</DialogTitle>
        <DialogContent dividers>
          <TextField
            multiline
            minRows={4}
            fullWidth
            label="Update your note"
            value={editNoteText}
            onChange={(e) => setEditNoteText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditNoteOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateNote}
            sx={{ bgcolor: GOLD, color: "#000" }}
          >
            Save changes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteNoteId}
        onClose={() => setDeleteNoteId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete note?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            This will permanently delete this note.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteNoteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteNote}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editReceiptOpen}
        onClose={() => setEditReceiptOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit receipt details</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="What type of purchase?"
              fullWidth
              value={editPurchaseType}
              onChange={(e) => setEditPurchaseType(e.target.value)}
            />
            <TextField
              label="Date purchased"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={editPurchaseDate}
              onChange={(e) => setEditPurchaseDate(e.target.value)}
              inputProps={{ min: GRANT_START_ISO, max: maxPurchaseDate }}
            />
            <TextField
              label="Amount"
              type="number"
              fullWidth
              value={editAmount}
              onChange={(e) => setEditAmount(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditReceiptOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateReceipt}
            sx={{ bgcolor: GOLD, color: "#000" }}
          >
            Save changes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteReceiptId}
        onClose={() => setDeleteReceiptId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete receipt?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            This will permanently delete the receipt record and remove the file
            from storage.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteReceiptId(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteReceipt}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
